import React from 'react'
import { Dialog, DialogContent, DialogTitle, IconButton, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { setSelectedTier } from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import { useQuote } from '../../../../context'
import PackageComparisonCards from './PackageComparisonCards'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    content: {
      padding: theme.spacing(3)
    }
  }),
  { name: 'PackageCompareDialog' }
)

export interface PackageCompareDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * "Compare plans" dialog — opened from the Total card's "Package Selected" Switch control. Shows the
 * three tier comparison cards ({@link PackageComparisonCards} in `switch` mode); picking a tier
 * applies it via `setSelectedTier` + the surrounding quote context (local recompute in
 * preview/customer, persists in the editor) and closes.
 */
export default function PackageCompareDialog({ open, onClose }: PackageCompareDialogProps) {
  const classes = useStyles({})
  const { quote, updateQuote } = useQuote()
  const { options } = useClientOptions()

  const handleSelect = (tierId: TieredPackageTierId) => {
    if (tierId !== quote.tieredPackages?.selectedTierId) {
      updateQuote({ quote: setSelectedTier({ quote, options, tierId }) })
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        rightContent={
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        }
      >
        Compare plans
      </DialogTitle>
      <DialogContent className={classes.content}>
        <PackageComparisonCards variant="switch" onSelectTier={handleSelect} />
      </DialogContent>
    </Dialog>
  )
}
